import { useActivation } from '@context/MobileOne';
import { ButtonPrimary, ScrollToTop } from '@core/index';
import { CardAutotopup } from '@core/CardAutotopup';
import { CounterButton } from '@core/CounterButton';
import { FormInputField } from '@core/FormInputField';
import { NavigationPageTitle } from '@core/NavigationPageTitle';
import { Notification } from '@core/Notification';
import { useStaticContent } from '@dom-digital-online-media/dom-static-content-sdk';
import { AppPageContentWrapper } from '@part/AppPageContentWrapper';
import { tA11y } from '@utils/a11y/a11yHelpers';
import {
  appAlert,
  appButtonType,
  appCardStyle,
  appChipStyle,
  appIconsPath,
  appPaymentProductType,
  getForbiddenCharError,
  validCharRegex
} from '@utils/globalConstant';
import React, { useEffect, useState } from 'react';

export function PaymentAmount() {
  // Contexts
  const {
    paymentProducts,
    tariffActivationForm,
    bookableTariffs,
    setStep9SubStep,
    processToPaymentForm,
    setProcessToPaymentForm,
    getTariffName
  } = useActivation();
  const { staticContentData, getStaticContentValue } = useStaticContent();

  // State
  const [isRecommendedTariff, setIsRecommendedTariff] = useState(false);
  const [staticProductList, setStaticProductList] = useState([]);
  const [selectedStaticProduct, setSelectedStaticProduct] = useState(null);
  const [directProduct, setDirectProduct] = useState({});
  const [inputAmount, setInputAmount] = useState(0);
  const [tariffAmount, setTariffAmount] = useState(null);

  const [isRangeError, setIsRangeError] = useState(false);
  const [isCharError, setIsCharError] = useState(false);

  // const
  const RECOMMENDED_TARIFF = staticContentData?.ek_alphacommPaymentAmounts?.recommendedTariffs;
  const STATIC_AMOUNT = staticContentData?.ek_alphacommPaymentAmounts?.staticAmounts;
  const tariffAmountButtonContent = tA11y('ek_reg_auto_top_up_amount_btn1');
  const inputAmountButtonContent = tA11y('ek_reg_auto_top_up_amount_btn2');
  const cardHeaderContent = tA11y('ek_reg_auto_top_up_amount_txt1');

  // Functions
  const validateAmountChange = (value) => {
    setSelectedStaticProduct(null);
    const amount = parseInt(value, 10);
    const isOutOfRange = amount < directProduct.minAmount || amount > directProduct.maxAmount;
    setIsRangeError(isOutOfRange);

    if (isCharError) {
      return false;
    }
    setInputAmount(amount);
    return true;
  };

  // Submit Handlers for different auto top-up amounts
  const handleTariffAmountSubmit = () => {
    setProcessToPaymentForm({
      ...processToPaymentForm,
      isDirectProduct: 'NO',
      productId: directProduct.id,
      productType: directProduct.productType,
      productAmount: tariffAmount
    });
    setStep9SubStep(3);
  };

  const handleDynamicAmountSubmit = () => {
    if (inputAmount && !isRangeError && !isCharError) {
      setProcessToPaymentForm({
        ...processToPaymentForm,
        isDirectProduct: 'NO',
        productId: directProduct.id,
        productType: directProduct.productType,
        productAmount: inputAmount
      });
      setStep9SubStep(3);
    } else {
      validateAmountChange(inputAmount);
    }
  };

  const handleStaticAmountSubmit = () => {
    // const selectedProduct = staticProductList.find((product) => product.id === selectedStaticProduct);
    setProcessToPaymentForm({
      ...processToPaymentForm,
      isDirectProduct: 'NO',
      productId: directProduct.id,
      productType: directProduct.productType,
      productAmount: selectedStaticProduct
    });
    setStep9SubStep(3);
  };

  // Hooks
  useEffect(() => {
    if (paymentProducts && paymentProducts.length > 0) {
      // Get automatic low balance products
      const productList = paymentProducts.filter(
        (product) => product.productType === appPaymentProductType.LOW_BALANCE
      );

      // Get direct product
      const findDirectProduct = productList.find(
        (product) => product.minAmount !== product.maxAmount
      );
      setDirectProduct(findDirectProduct);

      // Get static products
      // const filterStaticProducts = productList.filter(
      //   (product) =>
      //     product.minAmount === product.maxAmount && STATIC_AMOUNT.includes(product.amount)
      // );
      // const sortStaticProducts = filterStaticProducts.sort((a, b) => a.minAmount - b.minAmount);
      const filterStaticProducts = STATIC_AMOUNT.filter(
        (product) =>
          product >= findDirectProduct.minAmount && product <= findDirectProduct.maxAmount
      );
      const sortStaticProducts = filterStaticProducts.sort((a, b) => a - b);
      setStaticProductList(sortStaticProducts);
    }
    if (RECOMMENDED_TARIFF.includes(tariffActivationForm.chosenTariffId)) {
      setIsRecommendedTariff(true);
    }
    if (bookableTariffs.length > 0) {
      const tariff = bookableTariffs.find(({ id }) => id === tariffActivationForm.chosenTariffId);
      if (tariff) {
        const currentPrice = getStaticContentValue(tariff.price).content;
        if (currentPrice) {
          // const roundedAmount = String(Math.round(currentPrice.replace('€', '').replace(',', '.')));
          const numericPrice = parseFloat(currentPrice.replace('€', '').replace(',', '.'));
          let roundedAmount;

          if (numericPrice < 10) {
            roundedAmount = 10;
          } else {
            roundedAmount = Math.ceil(numericPrice / 5) * 5;
          }
          setTariffAmount(roundedAmount);
        }
      }
    }
  }, [paymentProducts, bookableTariffs, tariffActivationForm.chosenTariffId, staticContentData]);

  const getSpeedName = (tariffId) => {
    const tariff = staticContentData?.ek_tariff?.find((t) => t.id === tariffId);
    return tariff ? tariff.nameSpeed : '';
  }

  const tariffName = getTariffName(tariffActivationForm?.chosenTariffName, getSpeedName(tariffActivationForm?.chosenTariffId));

  return (
    <>
      <ScrollToTop>
        <div className="min-dvh-100 payment-data-container">
          <AppPageContentWrapper>
            <div className="w-100">
              <NavigationPageTitle title="ek_reg_auto_top_up_amount_hdl1" variant="sm" />
            </div>
            <div className="space-40" />

            {isRecommendedTariff ? (
              <CardAutotopup
                cardStyle={appCardStyle.PRIMARY_GRADIENT}
                chipIcon={appIconsPath.STAR}
                chipStyle={appChipStyle.HIGHLIGHT}
                chipLabel="ek_global_recommended_chip"
                text={cardHeaderContent.content.replace(
                  '{TARIFFNAME}',
                  tariffName.content
                )}
                buttonLabel={tariffAmountButtonContent.content.replace(
                  '{AMOUNT_ROUNDED}',
                  tariffAmount
                )}
                handleButtonClick={handleTariffAmountSubmit}
              />
            ) : (
              // Non Recommended Tariff Chip Amount Options
              <>
                <div
                  className="ek-copy-16-regular text-primary mb-20"
                  dangerouslySetInnerHTML={{
                    __html: tA11y('ek_reg_auto_top_up_amount_txt3').content
                  }}
                />
                <div className="row">
                  {staticProductList.map((product) => (
                    <div className="col" key={product}>
                      <CounterButton
                        id={product}
                        label={`${product} €`}
                        isSelected={selectedStaticProduct === product}
                        onSelect={() => {
                          setSelectedStaticProduct(product);
                          setIsCharError(false);
                          setIsRangeError(false);
                        }}
                      />
                    </div>
                  ))}
                </div>
                {/* <div className="row">
              {staticProductList.map((product) => (
                <div className="col" key={product.id}>
                  <CounterButton
                    id={product.id}
                    label={`${product.amount} €`}
                    isSelected={selectedStaticProduct === product.id}
                    onSelect={() => {
                      setSelectedStaticProduct(product.id);
                      setIsCharError(false);
                      setIsRangeError(false);
                    }}
                  />
                </div>
              ))}
            </div> */}
              </>
            )}

            <div className="d-flex align-items-center text-center my-40">
              <hr className="flex-grow-1 ms-16 me-12 bg-primary" />
              <div
                dangerouslySetInnerHTML={{
                  __html: tA11y('ek_reg_auto_top_up_amount_txt2').content
                }}
              />
              <hr className="flex-grow-1 ms-12 me-16 bg-primary" />
            </div>

            <div
              className="ek-copy-16-semibold text-primary mb-24"
              dangerouslySetInnerHTML={{
                __html: tA11y('ek_reg_auto_top_up_amount_hdl2')
                  .content.replace('{MIN_VALUE}', directProduct.minAmount)
                  .replace('{MAX_VALUE}', directProduct.maxAmount)
              }}
            />
            <FormInputField
              label="ek_global_amount_lbl"
              placeholder="ek_global_amount_plc"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              onChange={(e) => {
                setIsCharError(getForbiddenCharError(e.target.value, validCharRegex.PAYMENT_AMOUNT));
                validateAmountChange(e.target.value);
              }}
              onClear={() => {
                setIsCharError(getForbiddenCharError('', validCharRegex.PAYMENT_AMOUNT));
                validateAmountChange('');
              }}
              invalid={isRangeError || isCharError}
              checkCharRegex={validCharRegex.PAYMENT_AMOUNT}
            />
            {isRangeError && (
              <Notification
                type={appAlert.ERROR}
                message={tA11y('ek_global_amount_err')
                  .content.replace('{MIN_VALUE}', directProduct.minAmount)
                  .replace('{MAX_VALUE}', directProduct.maxAmount)}
              />
            )}
            {/* {isCharError && (
              <Notification
                type={appAlert.ERROR}
                message={tA11y('ek_global_err_addition_character').content}
              />
            )} */}
            <div className="w-100 text-primary mt-40">
              <div className="w-100 d-flex justify-content-center pb-40">
                <div className="col-xxl-5 col-lg-5 col-10 px-0">
                  <ButtonPrimary
                    buttonType={appButtonType.PRIMARY}
                    buttonContent={inputAmountButtonContent}
                    onClick={() =>
                      selectedStaticProduct
                        ? handleStaticAmountSubmit()
                        : handleDynamicAmountSubmit()
                    }
                  />
                </div>
              </div>
            </div>
          </AppPageContentWrapper>
        </div>
      </ScrollToTop>
    </>
  );
}

export default PaymentAmount;
